import React, { useState } from 'react';

import { sendClickEvent } from '@/utils/AnalyticsUtils';
import constants from '@/utils/constants';
import { dayjs } from '@/utils/dateTimeUtils';
import themeConfig from '@/theme/themeConfig';
import { Button, Modal, DatePicker, TimePicker } from '@/widgets';
import { ChevronDownIcon, DateRangeIcon } from '@/assets/icons';
import {
	HOME_DATE_FILTER_MOBILE_OPENED,
	HOME_DATE_FILTER_MOBILE_CLOSED,
} from '@/constants';

import styles from './CustomDateRangePickerMobile.module.scss';

const {
	FILTERS_DISPLAY_DATE_FORMAT,
	FILTERS_FUNCTIONAL_DATE_FORMAT,
	FILTERS_TIME_FORMAT,
} = constants;

const CustomDateRangePickerMobile = (props) => {
	const {
		startDate,
		activeDateRange,
		endDate,
		showTimePicker = true,
		container = '',
		onApplyClick,
		onCancelClick,
	} = props;

	const [localStartDate, setLocalStartDate] = useState(startDate);
	const [localEndDate, setLocalEndDate] = useState(endDate);
	const [modalStartDate, setModalStartDate] = useState(startDate);
	const [modalEndDate, setModalEndDate] = useState(endDate);
	const [showDatePickerModal, setShowDatePickerModal] = useState(false);
	const [datePickerType, setDatePickerType] = useState('from');

	const getContainerWiseEventName = (eventName) => {
		if (container === 'home') {
			switch (eventName) {
				case 'DATE_FILTER_MOBILE_OPENED':
					return HOME_DATE_FILTER_MOBILE_OPENED;
				case 'DATE_FILTER_MOBILE_CLOSED':
					return HOME_DATE_FILTER_MOBILE_CLOSED;
				default:
					return eventName;
			}
		}
		return eventName;
	};

	const _createDate = (obj, type) => {
		const { date, time } = obj;
		let _date = '';
		let _time = '';

		if (date) {
			_date = dayjs(date).format(FILTERS_FUNCTIONAL_DATE_FORMAT);
			_time = dayjs(
				type === 'from' ? dayjs(modalStartDate).startOf('day') : modalEndDate,
			).format(FILTERS_TIME_FORMAT);
		} else {
			_time = dayjs(time).format(FILTERS_TIME_FORMAT);
			_date = dayjs(type === 'from' ? modalStartDate : modalEndDate).format(
				FILTERS_FUNCTIONAL_DATE_FORMAT,
			);
		}
		return dayjs(`${_date} ${_time}`);
	};

	const disabledDate = (current, datePickerType) => {
		// Can not select days before activeFrom and activeTo
		let activeFrom = activeDateRange[0];
		let activeTo = activeDateRange[1].endOf('day');

		if (
			datePickerType === 'to' &&
			dayjs(localStartDate).isSameOrAfter(activeDateRange[0])
		) {
			activeFrom = localStartDate;
		}
		if (
			datePickerType === 'from' &&
			dayjs(localEndDate).isSameOrBefore(activeDateRange[1])
		) {
			activeTo = localEndDate;
		}

		return current < activeFrom || current > activeTo;
	};

	const _toggleMobileDatePicker = (type) => {
		sendClickEvent({
			name: getContainerWiseEventName(
				`DATE_FILTER_MOBILE_${!showDatePickerModal ? 'OPENED' : 'CLOSED'}`,
			),
			value: type || datePickerType,
		});
		setShowDatePickerModal(!showDatePickerModal);
		setDatePickerType(type);
	};

	const _getDefaultDate = () => {
		if (datePickerType === 'from') {
			return dayjs(localStartDate);
		}
		return dayjs(localEndDate);
	};

	const _getTimeValue = () => {
		if (datePickerType === 'from') {
			return dayjs(dayjs(modalStartDate), FILTERS_TIME_FORMAT);
		}
		return dayjs(dayjs(modalEndDate), FILTERS_TIME_FORMAT);
	};

	const _setModalMobileDate = (date) => {
		if (datePickerType === 'from') {
			const from = _createDate({ date }, 'from');
			setModalStartDate(from);
		} else {
			let to = _createDate({ date }, 'to');
			// The below if condition is to check if the date is before today's date then setting time to end of the day
			if (dayjs(to).isBefore(dayjs().startOf('day'))) {
				to = dayjs(to).endOf('day');
			} else {
				to = dayjs();
			}
			setModalEndDate(to);
		}
	};

	const _setModalMobileTime = (time) => {
		if (datePickerType === 'from') {
			const from = _createDate({ time }, 'from');
			setModalStartDate(from);
		} else {
			const to = _createDate({ time }, 'to');
			setModalEndDate(to);
		}
	};

	const _onModalApplyClick = () => {
		let _date = '';
		let _time = '';
		let _finalDateTime = '';

		if (datePickerType === 'from') {
			_date = dayjs(modalStartDate).format(FILTERS_FUNCTIONAL_DATE_FORMAT);
			_time = dayjs(modalStartDate).format(FILTERS_TIME_FORMAT);
			_finalDateTime = dayjs(`${_date} ${_time}`);
			setLocalStartDate(_finalDateTime);
		} else {
			_date = dayjs(modalEndDate).format(FILTERS_FUNCTIONAL_DATE_FORMAT);
			_time = dayjs(modalEndDate).format(FILTERS_TIME_FORMAT);
			_finalDateTime = dayjs(`${_date} ${_time}`);
			setLocalEndDate(_finalDateTime);
		}
		_toggleMobileDatePicker();
		return _finalDateTime;
	};

	const handleApplyClick = () => {
		onApplyClick({
			fromDate: localStartDate,
			toDate: localEndDate,
			selectedDateType: 'custom',
		});
	};

	const _handleModalCancel = () => {
		setModalStartDate(localStartDate);
		setModalEndDate(localEndDate);
		_toggleMobileDatePicker();
	};

	const _getCalendarContainer = () =>
		document.getElementById('dateTimeRangePickerContainerMobile');

	return (
		<div>
			<div>Start Date:</div>
			<Button
				onClick={() => _toggleMobileDatePicker('from')}
				className={styles.mobileDateButton}
			>
				<DateRangeIcon width={20} height={20} />
				<span>
					{dayjs(localStartDate).format(
						`${FILTERS_DISPLAY_DATE_FORMAT} | ${FILTERS_TIME_FORMAT}`,
					)}
				</span>
				<ChevronDownIcon width={20} height={20} />
			</Button>
			<div>End Date:</div>
			<Button
				onClick={() => _toggleMobileDatePicker('to')}
				className={styles.mobileDateButton}
			>
				<DateRangeIcon width={20} height={20} />
				<span>
					{dayjs(localEndDate).format(
						`${FILTERS_DISPLAY_DATE_FORMAT} | ${FILTERS_TIME_FORMAT}`,
					)}
				</span>
				<ChevronDownIcon width={20} height={20} />
			</Button>
			<div className={styles.footer}>
				<Button
					onClick={onCancelClick}
					className={styles.cancelButton}
					variant="tertiary"
				>
					Cancel
				</Button>
				<Button
					onClick={handleApplyClick}
					className={styles.applyButton}
					variant="primary"
				>
					Apply
				</Button>
			</div>
			<div id="dateTimeRangePickerContainerMobile">
				<Modal
					getContainer={_getCalendarContainer()}
					open={showDatePickerModal}
					zIndex={themeConfig.zIndexModalBase}
					onCancel={_handleModalCancel}
					closable
					onOk={_onModalApplyClick}
					width={300}
					destroyOnClose
					className={styles.modalContainer}
				>
					<DatePicker
						getPopupContainer={() =>
							document.getElementById('mobileDatePickerPopup')
						}
						popupClassName={styles.datePickerContainer}
						showNow={false}
						inputReadOnly
						format={FILTERS_DISPLAY_DATE_FORMAT}
						onChange={_setModalMobileDate}
						allowClear={false}
						placeholder="From"
						open
						showToday={false}
						disabledDate={(current) => disabledDate(current, datePickerType)}
						defaultValue={_getDefaultDate()}
					/>
					<div
						id="mobileDatePickerPopup"
						className={styles.mobileDatePickerPopup}
					/>
					{showTimePicker && (
						<div>
							<TimePicker
								getPopupContainer={() =>
									document.getElementById('mobileDatePickerPopup')
								}
								value={_getTimeValue()}
								onChange={_setModalMobileTime}
								showNow={false}
								format={FILTERS_TIME_FORMAT}
								allowClear={false}
								popupClassName={styles.timePickerPopup}
							/>
						</div>
					)}
				</Modal>
			</div>
		</div>
	);
};

export default CustomDateRangePickerMobile;
